import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Alert, CircularProgress, ToggleButton, ToggleButtonGroup, Card, CardContent, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';

import { reportingCapacityAPI } from '../services/reportingCapacityApi';
import ReportingStackedBars from '../components/ReportingStackedBars';

const PERIODS = { last_month: 'Last month', last_year: 'Last 12 months', all_time: 'All time' };
const num = (n) => Number(n || 0).toLocaleString();
const short = (d) => new Date(d).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
const long = (d) => new Date(d).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
const errText = (err, fallback) => err.response?.data?.detail || fallback;

function Stat({ label, value, sub }) {
  return (
    <Card>
      <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h5" component="div" noWrap>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{sub || ' '}</Typography>
      </CardContent>
    </Card>
  );
}

export default function ReportingDbSizes() {
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState('');
  const [dimension, setDimension] = useState('last_year');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    reportingCapacityAPI.dbSizesSummary().then((res) => setSummary(res.data)).catch((err) => setError(errText(err, 'Could not load database sizes')));
  }, []);

  useEffect(() => {
    if (!selected) { setData(null); return undefined; }
    let live = true;
    setLoading(true); setError(''); setData(null);
    reportingCapacityAPI.dbSizes(selected, dimension)
      .then((res) => live && setData(res.data))
      .catch((err) => live && setError(errText(err, 'Could not load database sizes')))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [selected, dimension]);

  const customers = [...(summary?.customers || [])].sort((a, b) => a.cust_name.localeCompare(b.cust_name));
  const months = data?.months || [];
  const instances = [...new Set(months.flatMap((m) => m.instances.map((i) => i.db_instance)))];
  const latest = months[months.length - 1];
  const bars = months.map((m) => ({ label: short(m.report_date), values: Object.fromEntries(m.instances.map((i) => [i.db_instance, i.totalsize_gb])) }));

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Database sizes</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        SQL Server database size per customer from the monthly report, with the log and row split for each instance.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {summary && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
          <Stat label="Total database size" value={`${num(summary.total_gb)} GB`} sub={summary.latest_report_date ? `report of ${summary.latest_report_date}` : ''} />
          <Stat label="Customers with data" value={num(summary.customer_count)} />
          <Stat label="Largest customer" value={summary.customers[0]?.cust_name || '-'} sub={summary.customers[0] ? `${num(summary.customers[0].total_gb)} GB` : ''} />
        </Box>
      )}
      {!summary && !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
      {summary && (
        <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField select size="small" label="Customer" value={selected} onChange={(e) => setSelected(e.target.value)} sx={{ minWidth: 260 }}>
            <MenuItem value="">Select a customer...</MenuItem>
            {customers.map((c) => <MenuItem key={c.cust_code} value={c.cust_code}>{c.cust_name}</MenuItem>)}
          </TextField>
          <ToggleButtonGroup exclusive size="small" value={dimension} onChange={(e, v) => v && setDimension(v)} aria-label="Period">
            {Object.entries(PERIODS).map(([k, label]) => <ToggleButton key={k} value={k}>{label}</ToggleButton>)}
          </ToggleButtonGroup>
        </Paper>
      )}
      {summary && !selected && (
        <Paper sx={{ minWidth: 0 }}>
          <TableContainer sx={{ maxHeight: 460 }}>
            <Table size="small" stickyHeader aria-label="Database size by customer">
              <TableHead><TableRow><TableCell>Customer</TableCell><TableCell>Location</TableCell><TableCell align="right">Total (GB)</TableCell></TableRow></TableHead>
              <TableBody>
                {summary.customers.map((c) => (
                  <TableRow key={c.cust_code} hover sx={{ cursor: 'pointer' }} onClick={() => setSelected(c.cust_code)}>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{c.cust_name}</Typography><Typography variant="caption" color="text.secondary">{c.cust_code}</Typography></TableCell>
                    <TableCell>{c.location || '-'}</TableCell>
                    <TableCell align="right">{num(c.total_gb)}</TableCell>
                  </TableRow>
                ))}
                {summary.customers.length === 0 && <TableRow><TableCell colSpan={3}>No database size data yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
      {data && !loading && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
            <Stat label="Current total size" value={`${num(latest?.total_gb)} GB`} sub={latest?.report_date} />
            <Stat label="Environments" value={instances.length} sub="instances tracked" />
            <Stat label="Months of data" value={data.month_count} />
          </Box>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>{data.customer.cust_name}: database size over time (GB)</Typography>
            {bars.length ? <ReportingStackedBars bars={bars} keys={instances} ariaLabel="Database size over time by instance" height={260} /> : <Typography color="text.secondary">No reports in this period.</Typography>}
          </Paper>
          {latest && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 2, alignItems: 'start' }}>
              <Paper sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ p: 2, pb: 0 }}>Latest breakdown, {latest.report_date}</Typography>
                <TableContainer>
                  <Table size="small" aria-label="Latest breakdown">
                    <TableHead><TableRow><TableCell>Instance</TableCell><TableCell>Region</TableCell><TableCell align="right">Log (MB)</TableCell><TableCell align="right">Row (MB)</TableCell><TableCell align="right">Total (MB)</TableCell><TableCell align="right">Total (GB)</TableCell></TableRow></TableHead>
                    <TableBody>
                      {[...latest.instances].sort((a, b) => b.totalsize_gb - a.totalsize_gb).map((i) => (
                        <TableRow key={i.db_instance} hover>
                          <TableCell sx={{ fontWeight: 500 }}>{i.db_instance}</TableCell><TableCell>{i.region || '-'}</TableCell>
                          <TableCell align="right">{num(i.logsize_mb)}</TableCell><TableCell align="right">{num(i.rowsize_mb)}</TableCell>
                          <TableCell align="right">{num(i.totalsize_mb)}</TableCell><TableCell align="right">{num(i.totalsize_gb)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow><TableCell colSpan={5} align="right" sx={{ fontWeight: 600 }}>Total</TableCell><TableCell align="right" sx={{ fontWeight: 600 }}>{num(latest.total_gb)} GB</TableCell></TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
              <Paper sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ p: 2, pb: 0 }}>Monthly size history</Typography>
                <TableContainer sx={{ maxHeight: 360 }}>
                  <Table size="small" stickyHeader aria-label="Monthly size history">
                    <TableHead><TableRow><TableCell>Month</TableCell><TableCell align="right">Total (GB)</TableCell><TableCell>Instances</TableCell></TableRow></TableHead>
                    <TableBody>
                      {[...months].reverse().map((m) => (
                        <TableRow key={m.report_date} hover>
                          <TableCell>{long(m.report_date)}</TableCell><TableCell align="right">{num(m.total_gb)}</TableCell>
                          <TableCell><Typography variant="caption" color="text.secondary">{m.instances.map((i) => i.db_instance).join(', ')}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
